import {
  Alert,
  AlertIcon,
  AlertDescription,
  Button,
  Flex,
  Skeleton,
} from '@chakra-ui/react';
import React from 'react';
import { Navigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { z } from 'zod';

import { useGetCurrentFundingRequestQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { Typography } from 'packages/common-frontend/components/Typography';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { PermissionGuard, PermissionsFn } from '~/core/PermissionGuard';
import { Breadcrumbs } from '~/packages/common-frontend/components/Breadcrumbs';
import { FundingRequestTemplateProviderV1 } from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';
import { ActionsButton } from '~/packages/funding-requests/ui/submit/ActionsButton';
import { SubmitFundingRequest } from '~/packages/funding-requests/ui/submit/index';

// Types
const RouteParamsStruct = z.object({
  creditFacilityId: z.string({
    message: 'Missing credit facility ID query parameter',
  }),
  loanId: z.string({
    message: 'Missing loanId ID query parameter',
  }),
  fundingRequestIdToDuplicate: z.string().optional(),
});

// Fragments
const RouteBase = () => {
  const { state: locationState } = useLocation();
  const flags = useFeatureFlags();

  const [queryParams] = useSearchParams();
  const {
    creditFacilityId,
    loanId,
    fundingRequestIdToDuplicate,
  } = RouteParamsStruct.parse(Object.fromEntries(queryParams));

  const { facilities } = useCreditFacilitiesOrThrow();
  const creditFacility = facilities.find(
    (facility) => facility.id === creditFacilityId,
  );

  const { data, loading: isLoading } = useGetCurrentFundingRequestQuery({
    fetchPolicy: 'no-cache',
    variables: { creditFacilityId },
  });

  if (isLoading) {
    return <Skeleton height="100vh" width="100%" />;
  }

  if (
    data?.currentFundingRequest &&
    data.currentFundingRequest.status !== 'pending'
  ) {
    return (
      <Navigate
        to={`/funding-requests/${data.currentFundingRequest.id}`}
        state={{ from: '/funding-requests/upsize' }}
      />
    );
  }

  if (
    data?.currentFundingRequest &&
    data.currentFundingRequest.status === 'pending' &&
    data.currentFundingRequest.type === 'origination'
  ) {
    return (
      <Flex direction="column" gap="1.5rem">
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Funding Requests', path: '/funding-requests' },
            // @jendevelops TODO: update breadcrumb to include loan id
            { label: creditFacility?.name || 'Upsize Funding Request' },
          ]}
        />
        <Typography variant="page-title">Upsize Funding Request</Typography>
        <Alert status="warning">
          <AlertIcon />
          <AlertDescription>
            A funding request for{' '}
            {creditFacility?.name || 'the selected facility'} has been
            submitted. The{' '}
            <Button
              variant="link"
              as={Link}
              to={`/funding-requests/${data.currentFundingRequest.id}`}
            >
              existing funding request
            </Button>{' '}
            must be approved before submitting a new one, or canceled to proceed
            with a new funding request.
          </AlertDescription>
        </Alert>
      </Flex>
    );
  }

  const loans = data?.currentFundingRequest?.loans ?? [];

  // This probably won't happen, but needs to be captured
  if (data?.currentFundingRequest && loans.length !== 1) {
    return (
      <Navigate
        to={`/funding-requests/${data.currentFundingRequest.id}`}
        state={{ from: '/funding-requests/upsize' }}
      />
    );
  }

  const loan = loans[0];

  if (
    data?.currentFundingRequest &&
    data.currentFundingRequest.status === 'pending' &&
    data.currentFundingRequest.type === 'upsizing' &&
    loan.id !== loanId
  ) {
    return (
      <Flex direction="column" gap="1.5rem">
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Funding Requests', path: '/funding-requests' },
            // @jendevelops TODO: update breadcrumb to include loan id
            { label: creditFacility?.name || 'Upsize Funding Request' },
          ]}
        />
        <Typography variant="page-title">Upsize Funding Request</Typography>
        <Alert status="warning">
          <AlertIcon />
          <AlertDescription>
            An upsize funding request has already been initiated for{' '}
            {creditFacility?.name || 'the selected facility'}, loan ID{' '}
            {loan.externalLoanId || loan.id}. To upsize a different loan, please
            cancel or complete the{' '}
            <Button
              variant="link"
              as={Link}
              to={`/funding-requests/upsize?creditFacilityId=${creditFacilityId}&loanId=${loan.id}`}
            >
              existing funding request
            </Button>{' '}
            before creating a new request.
          </AlertDescription>
        </Alert>
      </Flex>
    );
  }

  if (flags.displayFundingRequestV2ProviderV2) {
    return <p>Hi I am a placeholder for the new provider route for /upsize</p>;
  }

  if (flags.displayFundingRequestV2) {
    return (
      <FundingRequestTemplateProviderV1
        mode="upsize"
        allowedActions="mutable"
        creditFacilityId={creditFacilityId}
        loanId={loanId}
        fundingRequestIdToDuplicate={fundingRequestIdToDuplicate}
      >
        <Flex direction="column" gap="1.5rem">
          <Breadcrumbs
            breadcrumbs={[
              { label: 'Funding Requests', path: '/funding-requests' },
              // @jendevelops TODO: update breadcrumb to include loan id
              { label: creditFacility?.name || 'Upsize Funding Request' },
            ]}
          />
          <Flex justify="space-between">
            <Typography variant="page-title">Upsize Funding Request</Typography>
            <ActionsButton />
          </Flex>
        </Flex>
        {locationState?.from &&
          locationState.from === 'resubmit funding request' &&
          fundingRequestIdToDuplicate && (
            <Alert status="info" mb="1.5rem">
              <AlertIcon />
              <AlertDescription>
                This funding request is a resubmission from Funding Request{' '}
                <Button
                  variant="link"
                  as={Link}
                  to={`/funding-requests/${fundingRequestIdToDuplicate}`}
                >
                  {fundingRequestIdToDuplicate}
                </Button>
              </AlertDescription>
            </Alert>
          )}
        {/* @jendevelops Update to get the type from context, since create route
      template provider is sometimes responsible for initing the FR if it doesn't exist */}
        <SubmitFundingRequest type="upsizing" />
      </FundingRequestTemplateProviderV1>
    );
  }

  return (
    // if no frv2 flag + not authorized to upsize, even if permission given
    <Navigate
      to="/funding-requests"
      state={{ from: '/funding-requests/upsize' }}
    />
  );
};

// Exports
export const permissions: PermissionsFn = (context) => {
  if (context.user.userType === 'CAPITAL_PROVIDER') {
    return context.flags.productsFundingRequestsLenderUpsizePermissions;
  }
  return context.flags.productsFundingRequestsUpsizePermissions;
};

export const Route = () => (
  <PermissionGuard
    permissions={permissions}
    fallback={<Navigate to="/funding-requests" replace />}
  >
    <RouteErrorBoundary
      fallback={({ message, showErrorToast }) => {
        showErrorToast(message);
        return <Navigate to="/funding-requests" />;
      }}
    >
      <Flex
        direction="column"
        padding={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}
      >
        <RouteBase />
      </Flex>
    </RouteErrorBoundary>
  </PermissionGuard>
);
